import { Libraries, useLoadScript } from "@react-google-maps/api";
import clsx from "clsx";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";

import { OnSelectStoreCallback, StoreLocatorProvider } from "../../../contexts/store-locator";
import { useBreakpoints } from "../../../hooks/use-breakpoints";
import { useDataLayer } from "../../../hooks/use-data-layer";
import { useStoreLocator } from "../../../hooks/use-store-locator";
import { GoogleAnalyticsEvent } from "../../../utils/analytics";
import { Modal } from "../../modal";
import { Spinner } from "../../spinner";
import { StoreLocatorMap } from "./store-locator-map";
import { mapOptions } from "./store-locator-map/map-options";
import { StoreLocatorPanel, StoreLocatorPanelProps } from "./store-locator-panel";
import { StoreDetail } from "./store-locator-panel/store-detail";
import { StoresListBody } from "./store-locator-panel/stores-list-body";
import { StoresListHeader } from "./store-locator-panel/stores-list-header";

export interface StoreLocatorProps {
  stores: Queries.CompanySiteDetailDataFragment[];
  labels: StoreLocatorPanelProps["labels"] & {
    "store-locator-map-title": string;
    "store-locator-list-title": string;
  };
}

const mapLibraries: Libraries = ["places", "marker"]; // keep this static to avoid unnecessary reloading

export const StoreLocator: FC<StoreLocatorProps> = (props) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.GATSBY_GOOGLE_MAPS_API_KEY ?? "",
    libraries: mapLibraries,
    mapIds: mapOptions.mapId ? [mapOptions.mapId] : undefined,
  });

  const { lg } = useBreakpoints();
  const onSelectStore = useCallback<OnSelectStoreCallback>(
    (selectedStore, map) => {
      if (selectedStore) {
        if (map) {
          map.setCenter({
            lat: selectedStore.lat ?? 0,
            lng: selectedStore.lng ?? 0,
          });
          if (lg) {
            map.panBy(-window.innerWidth / 4, 0);
          }
        }
      }
    },
    [lg]
  );

  if (!isLoaded) {
    return (
      <div className="w-full flex justify-center items-center h-screen">
        <Spinner size="large" />
      </div>
    );
  }
  if (loadError) {
    return <p>Error while loading map</p>;
  }

  return (
    <StoreLocatorProvider {...props} onSelectStore={onSelectStore}>
      <StoreLocatorInner {...props} />
    </StoreLocatorProvider>
  );
};

enum StoreLocatorTabs {
  Map = "map",
  List = "list",
}

const StoreLocatorInner: FC<StoreLocatorProps> = ({ labels }) => {
  const { setMap, filteredStores, selectedStore, setSelectedStoreId } = useStoreLocator();

  const { lg } = useBreakpoints();
  const [visibleTab, setVisibleTab] = useState<StoreLocatorTabs>(StoreLocatorTabs.List);

  const { pushEvent } = useDataLayer();
  useEffect(() => {
    if (selectedStore) {
      pushEvent({
        event: GoogleAnalyticsEvent.ClickLocation,
        location: selectedStore.city,
        locator_type: selectedStore.siteType,
      });
    }
  }, [selectedStore, pushEvent]);

  const tabsConfig = useMemo<Array<{ id: StoreLocatorTabs; label: string }>>(() => {
    return [
      {
        id: StoreLocatorTabs.List,
        label: labels["store-locator-list-title"],
      },
      {
        id: StoreLocatorTabs.Map,
        label: labels["store-locator-map-title"],
      },
    ];
  }, [labels]);

  return (
    <div className="relative">
      <StoresListHeader labels={labels} className="lg:hidden" />
      <div className="flex items-center gap-4 lg:hidden p-3 font-light">
        {tabsConfig.map(({ id, label }) => (
          <div
            key={id}
            className={clsx(visibleTab === id && "underline text-primary")}
            onClick={() => setVisibleTab(id)}
          >
            {label}
          </div>
        ))}
      </div>
      <div className={clsx("w-full lg:hidden", visibleTab === "list" && "block")}>
        <StoresListBody labels={labels} />
      </div>
      <div className={clsx(visibleTab !== "map" && "hidden lg:block")}>
        <StoreLocatorMap onLoad={setMap} />
      </div>
      {/* Floating panel (desktop version of detail) */}
      <div className="absolute p-4 top-0 left-0 h-full hidden lg:block">
        <StoreLocatorPanel stores={filteredStores} labels={labels} className="h-full" />
      </div>
      {/* Modal (mobile version of detail) */}
      <Modal isOpen={!lg && !!selectedStore} onClose={() => setSelectedStoreId(null)}>
        {selectedStore && (
          <StoreDetail
            className="w-[285px] h-fit text-left"
            labels={labels}
            store={selectedStore}
            onClose={() => setSelectedStoreId(null)}
          />
        )}
      </Modal>
    </div>
  );
};
